import { faLock, faSignOut, faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
    BoxProps,
    Button,
    IconButton,
    InputAdornment,
    Box as MuiBox,
    styled,
    TextField,
    Theme,
    useMediaQuery,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { mutationLogin } from "src/api/auth";
import { MapContext } from "src/contexts/map";
import { URL_MAP_VIEW } from "src/router/routes";
import { Type_User } from "src/types/user";
import { loginSchema } from "src/validators/login";

const Box = styled(MuiBox)<BoxProps & { ismobile: string }>(
    ({ theme, ismobile }) => ({
        width: ismobile === "true" ? theme.spacing(30) : theme.spacing(40),
    }),
);

const LoginForm = () => {
    const [showPassword, setShowPassword] = useState(false);
    const { updateAuthStatus } = useContext(MapContext);
    const { t } = useTranslation();

    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: "",
            password: "",
        },
        resolver: yupResolver(loginSchema(t)),
    });

    const { mutateAsync: mutateLogin } = mutationLogin() || {};
    const { schema } = useParams<{ schema: string }>();
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    const onSubmit = async (data: Omit<Type_User, "id">) => {
        const response = await mutateLogin(data);
        if (response) {
            sessionStorage.setItem("authToken", response.data.token);
            navigate(`/${schema}${URL_MAP_VIEW}`);
            updateAuthStatus(true);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            ismobile={isMobile.toString()}
        >
            <Controller
                name="email"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label={t("login.email")}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon icon={faUser} />
                                </InputAdornment>
                            ),
                        }}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />
                )}
            />

            <Controller
                name="password"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        type={showPassword ? "text" : "password"}
                        label={t("login.password")}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon icon={faLock} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <VisibilityOffOutlined />
                                        ) : (
                                            <VisibilityOutlined />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                    />
                )}
            />

            <Button
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                endIcon={<FontAwesomeIcon icon={faSignOut} />}
            >
                {t("login.connect")}
            </Button>
        </Box>
    );
};

export default LoginForm;
